/**
 * SkillsPage — admin view for tenant Skills.
 *
 *   GET    /v1/skills            → list (SkillSummary rows)
 *   POST   /v1/skills:get        → version + deployment detail per skill
 *   POST   /v1/skills:upload-url → presigned PUT (mints object key)
 *   POST   /v1/skills:finalize-upload → server validates + creates SkillVersion
 *   POST   /v1/skills:deploy / :undeploy → fan-out targeting
 *   POST   /v1/skills:archive / :unarchive / :delete
 *
 * MVP scope: list + upload + deploy + archive/unarchive + delete. The
 * legacy SkillDetail (per-version timeline + per-vendor outcomes) is
 * deferred — surfacing the row-level state badges here is the minimum
 * useful read for ops.
 */
import { useCallback, useEffect, useState } from "react";
import DataTable from "../components/DataTable.jsx";
import ColumnPicker from "../components/ColumnPicker.jsx";
import OverflowTooltip from "../components/OverflowTooltip.jsx";
import OverlayDialog from "../components/OverlayDialog.jsx";
import SplitPage from "../components/SplitPage.jsx";
import TabStrip from "../components/TabStrip.jsx";
import SkillUploadDialog from "../components/skills/SkillUploadDialog.jsx";
import SkillDeployDialog from "../components/skills/SkillDeployDialog.jsx";
import DeleteSkillDialog from "../components/skills/DeleteSkillDialog.jsx";
import ObservedSkillsTable from "../components/skills/ObservedSkillsTable.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { ACTION_TEXT_CLASS, NOTE_CLASS } from "../lib/ui.js";
import { formatCompact } from "../lib/renderers.js";
import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { isSurfaceDenied, useMyPermissions } from "../lib/permissions.js";
import {
    applyArtifactRolloutCommand,
    listArtifactRollouts,
} from "../lib/artifactRolloutClient.js";
import {
    listSkills,
    getSkill,
    listSkillDeploymentStatuses,
    archiveSkill,
    unarchiveSkill,
    deleteSkill,
    publishSkillVersion,
    undeploySkill,
    updateSkillDisplayDisambiguator,
    skillStateLabel,
    skillDeploymentStatusLabel,
    skillOriginLabel,
    skillVendorLabel,
    SKILL_VERSION_STATE,
    SKILL_ORIGIN,
    SKILL_VENDOR,
} from "../lib/skillsClient.js";
import { canControlDelivery, deliveryControlCommand, deliveryControlCommands, deliveryStopped, hasHoldableState } from "../lib/artifactRolloutControls.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

// Origin/vendor options for the Observed-tab filter selects.
const ORIGIN_FILTER_OPTIONS = [
    SKILL_ORIGIN.USER_LOCAL,
    SKILL_ORIGIN.PROJECT_LOCAL,
    SKILL_ORIGIN.PLUGIN,
    SKILL_ORIGIN.ORIGIN_MANAGED,
    SKILL_ORIGIN.BUILT_IN,
];
const VENDOR_FILTER_OPTIONS = [SKILL_VENDOR.CLAUDE, SKILL_VENDOR.CURSOR, SKILL_VENDOR.CODEX];

const COLUMNS = [
    { id: "name",        label: "Name",            width: 260 },
    { id: "latest",      label: "Latest version",  width: 130 },
    { id: "state",       label: "State",           width: 110 },
    { id: "updated",     label: "Updated",         width: 150 },
];

function flattenSkill(s) {
    const latest = s.latestVersion ?? s.latest_version ?? null;
    return {
        id:           s.id ?? "",
        name:         s.name ?? "",
        archived:     Boolean(s.archived),
        latestVersion: latest,
        latestVersionNumber: latest?.versionNumber ?? latest?.version_number ?? null,
        latestState:  latest?.state ?? null,
        updatedAt:    s.updatedAt ?? s.updated_at ?? latest?.createdAt ?? latest?.created_at ?? null,
        raw: s,
    };
}

function StateBadge({ state, archived }) {
    if (archived) {
        return (
            <span className="inline-flex items-center rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] font-medium text-[color:var(--muted)]">
                Archived
            </span>
        );
    }
    const palette =
        state === "SKILL_VERSION_STATE_PUBLISHED"
            ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
            : state === "SKILL_VERSION_STATE_OBSERVED"
            ? "border-cyan-400/40 text-cyan-300"
            : state === "SKILL_VERSION_STATE_DRAFT"
            ? "border-[color:var(--line)] text-[color:var(--muted)]"
            : "border-[color:var(--line)] text-[color:var(--muted-soft)]";
    return (
        <span className={`inline-flex items-center rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] font-medium ${palette}`}>
            {skillStateLabel(state)}
        </span>
    );
}

export default function SkillsPage({ viewDef }) {
    const user = useCurrentUser();
    const isAdmin = Boolean(user?.isPlatformAdmin || user?.currentTenant?.role === "TENANT_ROLE_ADMIN");
    const caps = useMyPermissions();
    const canViewObserved = isAdmin && !isSurfaceDenied(caps, "ListObservedSkills");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // overlay shape: null | "upload" | { mode: "deploy", skill, versions }
    const [overlay, setOverlay] = useState(null);

    // Managed | Observed tab. The Observed surface (O-769) is admin-only;
    // non-admins only ever see the Managed catalog.
    const [tab, setTab] = useState("managed");
    const activeTab = canViewObserved ? tab : "managed";
    // Observed-tab filters.
    const [obQuery, setObQuery] = useState("");
    const [obOrigin, setObOrigin] = useState("");
    const [obVendor, setObVendor] = useState("");
    const [obContentSha, setObContentSha] = useState("");

    const refetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await listSkills();
            const arr = Array.isArray(resp?.skills) ? resp.skills : [];
            setRows(arr.map(flattenSkill));
        } catch (err) {
            console.error("[skills] list", err);
            setError(err.message || String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refetch().catch(() => {}); }, [refetch]);

    // Action handlers exposed to the detail panel. Promise-returning so
    // the panel can show its own loading state; on success they refresh
    // the list (archive) or close + refresh (delete).
    const onToggleArchive = useCallback(async (skillId, archived) => {
        if (archived) await unarchiveSkill(skillId);
        else await archiveSkill(skillId);
        await refetch();
    }, [refetch]);

    const onDelete = useCallback(async (skillId) => {
        await deleteSkill(skillId);
        setRows((cur) => cur.filter((r) => r.id !== skillId));
    }, []);

    const onOpenDeploy = useCallback((skill, versions) => {
        setOverlay({ mode: "deploy", skill, versions });
    }, []);

    const cols = useDataTableColumns({
        storageKey: "skills",
        columns: COLUMNS,
        defaultVisible: COLUMNS.map((c) => c.id),
    });

    const renderCell = useCallback(
        (r, col) => {
            switch (col.id) {
                case "name":
                    return (
                        <OverflowTooltip
                            as="strong"
                            text={r.name || "—"}
                            className="text-[11px] leading-[1.08] font-medium"
                        />
                    );
                case "latest":
                    return (
                        <span className="text-[11px] font-mono text-[color:var(--muted)]">
                            {r.latestVersionNumber != null ? `v${r.latestVersionNumber}` : "—"}
                        </span>
                    );
                case "state":
                    return <StateBadge state={r.latestState} archived={r.archived} />;
                case "updated": {
                    if (!r.updatedAt) return <span className={`${NOTE_CLASS} text-[10px]`}>—</span>;
                    const d = new Date(r.updatedAt);
                    return (
                        <span className="text-[10px] text-[color:var(--muted)]">
                            {dayLabelFormatter.format(d)} {timeLabelFormatter.format(d)}
                        </span>
                    );
                }
                default:
                    return null;
            }
        },
        [],
    );

    // Click a row → drawer toggles. Re-clicking the selected row closes
    // it (matches EndpointInventoryPage / DirectoryBrowserTab).
    const [selected, setSelected] = useState(null);
    const onRowClick = useCallback((row) => {
        setSelected((cur) => (cur?.id === row.id ? null : row));
    }, []);
    const closeSelection = useCallback(() => setSelected(null), []);

    // Managed → Observed cross-link: jump to the Observed tab filtered by a
    // managed skill's root content hash, surfacing every endpoint-observed row
    // sharing the same SKILL.md body.
    const goToObservedSiblings = useCallback((rootHex) => {
        if (!rootHex) return;
        setObQuery("");
        setObOrigin("");
        setObVendor("");
        setObContentSha(rootHex);
        setTab("observed");
        setSelected(null);
    }, []);

    // When the row's underlying summary changes (refresh after archive
    // etc.), keep the selected reference in sync so the right pane reads
    // the latest fields.
    useEffect(() => {
        if (!selected) return;
        const fresh = rows.find((r) => r.id === selected.id);
        if (fresh && fresh !== selected) setSelected(fresh);
        if (!fresh) setSelected(null);
    }, [rows, selected]);

    // Match UserManagement / ProvisioningTokens vocabulary: a chip-styled
    // trigger that visually merges into the header (`border-b-0` so the
    // bottom edge bleeds into the header tab-strip baseline), plus the
    // ColumnPicker chip immediately to its right.
    const uploadAction = (
        <div className="flex items-center gap-[6px]">
            <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
                <button
                    type="button"
                    onClick={() => setOverlay("upload")}
                    aria-pressed={overlay === "upload"}
                    className="inline-flex items-center px-[10px] text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors cursor-pointer"
                >
                    <span className={`${ACTION_TEXT_CLASS} text-[color:var(--muted)]`}>
                        Upload skill
                    </span>
                </button>
            </div>
            <ColumnPicker {...cols.pickerProps} />
        </div>
    );

    // Single flat tab strip used as SplitPage's focusHeader — Managed /
    // Observed are the page's tabs (admin) rather than a second strip
    // stacked under a redundant "Skills" title tab. Mirrors
    // DirectoryIntegrationsPage (Integrations · Users · Groups · Devices).
    // Non-admins have no Observed surface, so they get a single titled tab.
    const header = (
        <TabStrip
            tabs={
                canViewObserved
                    ? [
                        { id: "managed",  label: "Managed",  active: activeTab === "managed",  dot: activeTab === "managed"  ? "bg-[color:var(--green)]" : undefined, onSelect: () => setTab("managed") },
                        { id: "observed", label: "Observed", active: activeTab === "observed", dot: activeTab === "observed" ? "bg-[color:var(--green)]" : undefined, onSelect: () => setTab("observed") },
                      ]
                    : [
                        { id: "managed", label: viewDef?.label ?? "Skills", active: true, dot: "bg-[color:var(--green)]" },
                      ]
            }
            actions={uploadAction}
            className="pt-[11px] max-[900px]:pt-[8px]"
        />
    );

    // Left column: stats anchored at the top, table scrolls below.
    // h-full (not flex-1) because SplitPage's column slot is a plain
    // block — see EndpointInventoryPage for the same reasoning.
    const managedLeft = (
        <>
            <div className="shrink-0 grid gap-[10px] pb-[2px]">
                <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                    <span>
                        <strong className="text-[color:var(--ink)]">{formatCompact(rows.length)}</strong>{" "}
                        {rows.length === 1 ? "skill" : "skills"}
                    </span>
                    {loading ? <span className={NOTE_CLASS}>Loading…</span> : null}
                    {error ? <span className="text-red-400 truncate max-w-[400px]" title={error}>{error}</span> : null}
                </div>
            </div>

            <div className="flex flex-1 min-h-0 min-w-0 flex-col">
                <DataTable
                    cols={cols}
                    rows={rows}
                    getRowId={(r) => r.id}
                    renderCell={renderCell}
                    onRowClick={onRowClick}
                    loading={loading}
                    skeletonRows={4}
                    empty={
                        <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px] my-[12px]">
                            <strong className="text-[13px] leading-[1.08] font-medium">No skills uploaded yet</strong>
                            <span className={NOTE_CLASS}>
                                Click <strong className="text-[color:var(--ink)] font-medium">Upload skill</strong> to add a <code>.skill</code> or <code>.zip</code> archive. Once uploaded, deploy it to all endpoints or a specific set.
                            </span>
                        </div>
                    }
                />
            </div>
        </>
    );

    const observedLeft = (
        <>
            <div className="shrink-0 flex flex-wrap items-center gap-[6px] border-y border-[color:var(--line-soft)] py-[6px]">
                <input
                    type="text"
                    value={obQuery}
                    onChange={(e) => setObQuery(e.target.value)}
                    placeholder="Search observed skills…"
                    className="min-h-[28px] min-w-[180px] flex-1 rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[11px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                />
                <select
                    value={obOrigin}
                    onChange={(e) => setObOrigin(e.target.value)}
                    className="min-h-[28px] rounded-[2px] border border-[color:var(--line)] px-[6px] text-[11px] text-[color:var(--muted)] outline-none cursor-pointer"
                >
                    <option value="">All origins</option>
                    {ORIGIN_FILTER_OPTIONS.map((o) => (
                        <option key={o} value={o}>{skillOriginLabel(o)}</option>
                    ))}
                </select>
                <select
                    value={obVendor}
                    onChange={(e) => setObVendor(e.target.value)}
                    className="min-h-[28px] rounded-[2px] border border-[color:var(--line)] px-[6px] text-[11px] text-[color:var(--muted)] outline-none cursor-pointer"
                >
                    <option value="">All vendors</option>
                    {VENDOR_FILTER_OPTIONS.map((v) => (
                        <option key={v} value={v}>{skillVendorLabel(v)}</option>
                    ))}
                </select>
                {obContentSha ? (
                    <button
                        type="button"
                        onClick={() => setObContentSha("")}
                        className="min-h-[28px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                        title="Clear the content-hash filter"
                    >
                        Clear hash filter
                    </button>
                ) : null}
            </div>
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto pt-[8px]">
                <ObservedSkillsTable
                    query={obQuery}
                    origin={obOrigin || null}
                    vendor={obVendor || null}
                    contentSha256={obContentSha || null}
                    isAdmin={isAdmin}
                />
            </div>
        </>
    );

    const leftContent = (
        <div className="flex h-full min-h-0 min-w-0 flex-col py-[8px]">
            {activeTab === "observed" ? observedLeft : managedLeft}
        </div>
    );

    // Right column: a single-tab header carrying the selected skill name
    // (SplitPage injects the close chevron into its actions slot) + the
    // detail body with versions / deployments / actions.
    const detailTabStrip = (
        <TabStrip
            tabs={[{
                id: "detail",
                label: selected?.name || selected?.id || "Skill",
                active: true,
                dot: "bg-[color:var(--green)]",
            }]}
            className="pt-0"
        />
    );

    const rightContent = () => (
        <div className="flex h-full min-h-0 min-w-0 flex-col">
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto py-[12px]">
                {selected ? (
                    <SkillDetailContent
                        skill={selected}
                        isAdmin={isAdmin}
                        canViewObserved={canViewObserved}
                        onArchive={() => onToggleArchive(selected.id, selected.archived)}
                        onDelete={async () => {
                            await onDelete(selected.id);
                            closeSelection();
                        }}
                        onDeploy={(versions) => onOpenDeploy(selected, versions)}
                        onObservedSiblings={goToObservedSiblings}
                    />
                ) : null}
            </div>
        </div>
    );

    return (
        <>
            <OverlayDialog
                open={overlay === "upload"}
                onClose={() => setOverlay(null)}
                analyticsId={DIALOG.SKILL_UPLOAD}
                eyebrow="Skills"
                title="Upload skill"
                width="560px"
            >
                {overlay === "upload" ? (
                    <SkillUploadDialog
                        onClose={() => setOverlay(null)}
                        onUploaded={() => refetch().catch(() => {})}
                    />
                ) : null}
            </OverlayDialog>

            <OverlayDialog
                open={overlay?.mode === "deploy"}
                onClose={() => setOverlay(null)}
                analyticsId={DIALOG.SKILL_DEPLOY}
                eyebrow="Skills"
                title="Deploy skill"
                width="560px"
            >
                {overlay?.mode === "deploy" ? (
                    <SkillDeployDialog
                        skill={overlay.skill}
                        versions={overlay.versions}
                        onClose={() => setOverlay(null)}
                        onDeployed={() => refetch().catch(() => {})}
                    />
                ) : null}
            </OverlayDialog>

            {/* AdminPage wraps the view in a px-[var(--main-inset)] section
                and SplitPage's ContentShell adds the same inset per column —
                the outer one would double up at the edges. Negative margin
                lets the column-level inset run alone (matches
                EndpointInventoryPage / DirectoryBrowserTab). */}
            <div className="flex-1 min-h-0 min-w-0 -mx-[var(--main-inset)]">
                <SplitPage
                    defaultDetailRatio={0.42}
                    focusHeader={header}
                    leftContent={leftContent}
                    rightHeader={detailTabStrip}
                    rightContent={rightContent}
                    forceCollapse={!selected}
                    onCloseDetail={closeSelection}
                />
            </div>
        </>
    );
}

// ── Right-pane helpers (mirror EndpointInventoryPage's DetailSection /
// DetailField vocabulary so the inspector reads the same as the
// endpoint drawer). ────────────────────────────────────────────────

function DetailField({ label, value, mono = false }) {
    return (
        <div className="grid gap-[2px] min-w-0">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>{label}</span>
            <span
                className={`text-[12px] leading-[1.32] text-[color:var(--ink)] break-words ${mono ? "font-mono" : ""}`}
                title={typeof value === "string" ? value : undefined}
            >
                {value || <span className="text-[color:var(--muted-soft)]">—</span>}
            </span>
        </div>
    );
}

function DetailSection({ title, children }) {
    return (
        <div className="grid gap-[10px]">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                {title}
            </span>
            <div className="grid gap-[10px] [grid-template-columns:repeat(2,minmax(0,1fr))]">
                {children}
            </div>
        </div>
    );
}

// Color-coded deployment status badge. Failed = red, partial = amber,
// installed = green, the rest fall back to muted. Keeps the per-endpoint
// status visually scannable at a glance.
function DeploymentStatusBadge({ status }) {
    const palette =
        status === "SKILL_DEPLOYMENT_STATUS_KIND_INSTALLED"             ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]" :
        status === "SKILL_DEPLOYMENT_STATUS_KIND_INSTALLED_NO_VENDORS"  ? "border-[color:var(--line)] text-[color:var(--muted)]" :
        status === "SKILL_DEPLOYMENT_STATUS_KIND_PARTIAL"               ? "border-amber-400 text-amber-400" :
        status === "SKILL_DEPLOYMENT_STATUS_KIND_PENDING"               ? "border-[color:var(--line)] text-[color:var(--muted)]" :
        status === "SKILL_DEPLOYMENT_STATUS_KIND_REMOVED"               ? "border-[color:var(--line)] text-[color:var(--muted-soft)]" :
        status === "SKILL_DEPLOYMENT_STATUS_KIND_FAILED"                ? "border-red-400 text-red-400" :
                                                                         "border-[color:var(--line)] text-[color:var(--muted-soft)]";
    return (
        <span className={`inline-flex items-center rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] font-medium ${palette}`}>
            {skillDeploymentStatusLabel(status)}
        </span>
    );
}

const formatTs = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return `${dayLabelFormatter.format(d)} ${timeLabelFormatter.format(d)}`;
};

// Skill detail body. Lazy-fetches GetSkill on mount/skill-change for the
// full description / versions / per-endpoint deployments + statuses
// (incl. download URLs and per-vendor outcomes), then renders sections +
// action buttons (Deploy, Archive/Unarchive, Delete with two-step confirm).
function SkillDetailContent({ skill, isAdmin, canViewObserved, onArchive, onDelete, onDeploy, onObservedSiblings }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionState, setActionState] = useState(null); // "archive-pending" | error string (delete handled separately via dialog)
    const [deleteOpen, setDeleteOpen]   = useState(false);
    // display_disambiguator inline editor (admin, O-770).
    const [disambigEditing, setDisambigEditing] = useState(false);
    const [disambigDraft, setDisambigDraft]     = useState("");
    const [disambigBusy, setDisambigBusy]       = useState(null); // null | "pending" | error string
    // Per-version + per-deployment busy maps so an in-flight publish or
    // revoke disables only its own row instead of the whole pane.
    const [versionBusy, setVersionBusy] = useState({}); // { [versionId]: "pending" | error string }
    const [deployBusy, setDeployBusy]   = useState({}); // { [endpointId]: "pending" | error string }
    const [rolloutBusy, setRolloutBusy] = useState({});
    const [rolloutPage, setRolloutPage] = useState(null);

    // O-2413: statuses live on their own paginated RPC now (the GetSkill
    // response field is empty post-rollout). We fetch the first page on
    // panel open and surface a truncation hint if `total` exceeds what
    // we loaded. The inline field on GetSkillResponse is still read below
    // as a fallback so a dashboard running against a pre-O-2413 backend
    // keeps working through the deploy window.
    const [statusesPage, setStatusesPage] = useState(null); // null | { deploymentStatuses, total, pageSize }

    const refetch = useCallback(async () => {
        if (!skill?.id) return;
        setLoading(true);
        setError(null);
        try {
            const [skillResp, statusesResp] = await Promise.all([
                getSkill(skill.id),
                listSkillDeploymentStatuses(skill.id).catch(() => null),
            ]);
            const componentId = skillResp?.rolloutComponentId ?? skillResp?.rollout_component_id;
            const rolloutResp = componentId
                ? await listArtifactRollouts({ componentId }).catch(() => null)
                : null;
            setData(skillResp ?? null);
            setStatusesPage(statusesResp ?? null);
            setRolloutPage(rolloutResp ?? null);
        } catch (err) {
            setError(err.message || String(err));
        } finally {
            setLoading(false);
        }
    }, [skill?.id]);

    useEffect(() => {
        if (!skill?.id) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        Promise.all([
            getSkill(skill.id),
            // Tolerate 404 here: an older backend (pre-O-2413) won't have
            // this RPC yet, and `getSkill` will still carry the legacy
            // inline statuses field. The fallback below picks them up.
            listSkillDeploymentStatuses(skill.id).catch(() => null),
        ])
            .then(async ([skillResp, statusesResp]) => {
                if (cancelled) return;
                const componentId = skillResp?.rolloutComponentId ?? skillResp?.rollout_component_id;
                const rolloutResp = componentId
                    ? await listArtifactRollouts({ componentId }).catch(() => null)
                    : null;
                if (cancelled) return;
                setData(skillResp ?? null);
                setStatusesPage(statusesResp ?? null);
                setRolloutPage(rolloutResp ?? null);
            })
            .catch((err) => { if (!cancelled) setError(err.message || String(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [skill?.id]);

    const handlePublish = useCallback(async (versionId) => {
        if (!skill?.id || !versionId) return;
        setVersionBusy((cur) => ({ ...cur, [versionId]: "pending" }));
        try {
            await publishSkillVersion({ skillId: skill.id, versionId });
            await refetch();
            setVersionBusy((cur) => {
                const next = { ...cur };
                delete next[versionId];
                return next;
            });
        } catch (err) {
            setVersionBusy((cur) => ({ ...cur, [versionId]: err.message || String(err) }));
        }
    }, [skill?.id, refetch]);

    const handleRevoke = useCallback(async (endpointId) => {
        if (!skill?.id || !endpointId) return;
        setDeployBusy((cur) => ({ ...cur, [endpointId]: "pending" }));
        try {
            await undeploySkill({
                skillId: skill.id,
                target: { kind: "endpoints", endpointIds: [endpointId] },
            });
            await refetch();
            setDeployBusy((cur) => {
                const next = { ...cur };
                delete next[endpointId];
                return next;
            });
        } catch (err) {
            setDeployBusy((cur) => ({ ...cur, [endpointId]: err.message || String(err) }));
        }
    }, [skill?.id, refetch]);

    const runRolloutCommand = useCallback(async (publicationId, commandName, value) => {
        setRolloutBusy((current) => ({ ...current, [publicationId]: "pending" }));
        let completed = 0;
        const commands = Array.isArray(value) ? value : [value];
        try {
            for (const command of commands) {
                await applyArtifactRolloutCommand(typeof commandName === "function" ? commandName(command) : commandName, command);
                completed += 1;
            }
            await refetch();
            setRolloutBusy((current) => {
                const next = { ...current };
                delete next[publicationId];
                return next;
            });
        } catch (err) {
            if (completed) await refetch();
            const detail = err.message || String(err);
            setRolloutBusy((current) => ({
                ...current,
                [publicationId]: completed ? `${completed} of ${commands.length} changes saved. ${detail}` : detail,
            }));
        }
    }, [refetch]);

    const skillFull   = data?.skill;
    const versions    = data?.versions ?? [];
    const deployments = data?.deployments ?? [];
    // Prefer the paginated O-2413 source (`statusesPage`) when present; fall
    // back to the deprecated inline `deployment_statuses` field that a
    // pre-O-2413 backend still populates. Once every prod backend ships
    // ListSkillDeploymentStatuses the fallback can be deleted.
    const statusesFromPage  = statusesPage?.deploymentStatuses ?? statusesPage?.deployment_statuses;
    const statusesFromInline = data?.deploymentStatuses ?? data?.deployment_statuses ?? [];
    const statuses    = statusesFromPage ?? statusesFromInline;
    // No client-side component filter: the request is already scoped by
    // `componentId` (see the listArtifactRollouts calls above), and filtering
    // again here compared two different encodings of the same id. The server
    // writes `component_key` as `'managed-skill:' || skill_id::text`, a
    // hyphenated UUID, while `skill.id` is the proto `bytes` field, which
    // grpc-gateway delivers as base64. The predicate was therefore never true,
    // so the panel always claimed "No rollout history" and every control below
    // it was unreachable.
    const rollouts    = rolloutPage?.rollouts ?? [];
    // Truthy when the new RPC returned more rows than we fetched. Surfacing
    // this matters because the panel renders the inline list flat — without
    // the note, a user with thousands of statuses would silently see only
    // the first page.
    const statusesTotal     = statusesPage?.total ?? null;
    const statusesTruncated = statusesTotal != null && statuses.length < statusesTotal;

    const description = skillFull?.description ?? skill.description ?? "";
    const createdBy   = skillFull?.createdByName ?? skillFull?.created_by_name ?? "";
    const createdAt   = skillFull?.createdAt ?? skillFull?.created_at;
    const updatedAt   = skillFull?.updatedAt ?? skillFull?.updated_at;
    const tags        = skillFull?.tags ?? [];
    const displayDisambiguator = skillFull?.displayDisambiguator ?? skillFull?.display_disambiguator ?? "";
    const disambiguatorIsAuto  = skillFull?.displayDisambiguatorIsAuto ?? skillFull?.display_disambiguator_is_auto;
    // Suppressed on the wire for legacy V1 rows (archive-hash root); the
    // cross-link hides itself when absent. See FinalizeSkillUpload lineage notes.
    const rootContentSha       = skillFull?.rootContentSha256 ?? skillFull?.root_content_sha256 ?? "";

    const handleArchive = useCallback(async () => {
        setActionState("archive-pending");
        try {
            await onArchive();
            setActionState(null);
        } catch (err) {
            setActionState(`archive-failed: ${err.message || err}`);
        }
    }, [onArchive]);

    const handleSaveDisambiguator = useCallback(async () => {
        setDisambigBusy("pending");
        try {
            // Blank input clears the override server-side (re-engages auto-derivation).
            await updateSkillDisplayDisambiguator(skill.id, disambigDraft);
            await refetch();
            setDisambigEditing(false);
            setDisambigBusy(null);
        } catch (err) {
            setDisambigBusy(err.message || String(err));
        }
    }, [skill?.id, disambigDraft, refetch]);

    return (
        <div className="grid gap-[16px]">
            {description ? (
                <div className="grid gap-[6px]">
                    <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                        Description
                    </span>
                    <p className="m-0 whitespace-pre-wrap text-[12px] leading-[1.5] text-[color:var(--ink)]">
                        {description}
                    </p>
                </div>
            ) : null}

            {description ? <div className="border-t border-[color:var(--line)]" /> : null}

            <DetailSection title="Skill">
                <DetailField label="Name" value={skillFull?.name ?? skill.name} />
                <DetailField label="State" value={skill.archived ? "Archived" : skillStateLabel(skill.latestState)} />
                <DetailField label="Created by" value={createdBy} />
                <DetailField label="Created" value={formatTs(createdAt)} />
                <DetailField label="Updated" value={formatTs(updatedAt)} />
                <DetailField label="Tags" value={tags.length ? tags.join(", ") : ""} />
                <DetailField label="Skill ID" value={skill.id} mono />
            </DetailSection>

            {isAdmin ? (
                <div className="grid gap-[6px]">
                    <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                        Display label
                    </span>
                    {disambigEditing ? (
                        <div className="flex items-center gap-[6px] flex-wrap">
                            <input
                                type="text"
                                value={disambigDraft}
                                onChange={(e) => setDisambigDraft(e.target.value)}
                                placeholder="Automatically derived. Leave blank to clear."
                                disabled={disambigBusy === "pending"}
                                className="min-h-[28px] flex-1 min-w-[160px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[12px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                            />
                            <button
                                type="button"
                                onClick={handleSaveDisambiguator}
                                disabled={disambigBusy === "pending"}
                                className="h-[28px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--ink)] font-medium disabled:opacity-50 cursor-pointer"
                            >
                                {disambigBusy === "pending" ? "Saving…" : "Save"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setDisambigEditing(false); setDisambigBusy(null); }}
                                disabled={disambigBusy === "pending"}
                                className="h-[28px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-[8px] flex-wrap">
                            <span className="text-[12px] text-[color:var(--ink)] font-mono break-all">
                                {displayDisambiguator || <span className="text-[color:var(--muted-soft)]">—</span>}
                            </span>
                            {disambiguatorIsAuto ? (
                                <span
                                    className="inline-flex items-center rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] font-medium text-[color:var(--muted-soft)]"
                                    title="Calculated from the author, description, and content hash. Set an override to keep it fixed."
                                >
                                    auto
                                </span>
                            ) : null}
                            <button
                                type="button"
                                onClick={() => {
                                    setDisambigDraft(disambiguatorIsAuto ? "" : displayDisambiguator);
                                    setDisambigBusy(null);
                                    setDisambigEditing(true);
                                }}
                                className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                            >
                                Edit
                            </button>
                        </div>
                    )}
                    {typeof disambigBusy === "string" && disambigBusy !== "pending" ? (
                        <span className="text-[10px] text-red-400" title={disambigBusy}>Failed to save label</span>
                    ) : null}
                    {rootContentSha && canViewObserved ? (
                        <button
                            type="button"
                            onClick={() => onObservedSiblings?.(rootContentSha)}
                            title="Find observed skills that match this skill's root content hash."
                            className="justify-self-start text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                        >
                            View observed siblings →
                        </button>
                    ) : null}
                </div>
            ) : null}

            <div className="border-t border-[color:var(--line)]" />

            <div className="grid gap-[8px]">
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                    Versions
                </span>
                {loading ? (
                    <span className={`${NOTE_CLASS} text-[11px]`}>Loading…</span>
                ) : error ? (
                    <span className="text-[11px] text-red-400" title={error}>Failed to load</span>
                ) : !versions.length ? (
                    <span className={`${NOTE_CLASS} text-[11px]`}>No versions.</span>
                ) : (
                    <ul className="grid gap-[8px]">
                        {versions.map((v) => {
                            const num         = v.versionNumber ?? v.version_number;
                            const size        = v.blobSizeBytes ?? v.blob_size_bytes;
                            const sha         = v.blobSha256    ?? v.blob_sha256;
                            const uploadedBy  = v.uploadedByName ?? v.uploaded_by_name;
                            const createdTs   = v.createdAt      ?? v.created_at;
                            const publishedTs = v.publishedAt    ?? v.published_at;
                            const dlUrl       = v.downloadUrl    ?? v.download_url;
                            return (
                                <li key={v.id} className="grid gap-[4px] border border-[color:var(--line)] px-[10px] py-[8px]">
                                    <div className="flex items-center justify-between gap-[8px]">
                                        <span className="flex items-center gap-[8px] min-w-0">
                                            <span className="font-mono text-[12px] text-[color:var(--ink)] font-medium">v{num}</span>
                                            <StateBadge state={v.state} />
                                            {size ? (
                                                <span className={`${NOTE_CLASS} text-[10px]`}>
                                                    {(Number(size) / 1024 / 1024).toFixed(2)} MB
                                                </span>
                                            ) : null}
                                        </span>
                                        <span className="flex items-center gap-[8px]">
                                            {v.state === SKILL_VERSION_STATE.DRAFT ? (
                                                versionBusy[v.id] === "pending" ? (
                                                    <span className={`${NOTE_CLASS} text-[10px]`}>Publishing…</span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handlePublish(v.id)}
                                                        className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                                                        title="Publish this draft. It replaces the previously published version."
                                                    >
                                                        Publish
                                                    </button>
                                                )
                                            ) : null}
                                            {dlUrl ? (
                                                <a
                                                    href={dlUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                                                >
                                                    Download
                                                </a>
                                            ) : null}
                                        </span>
                                    </div>
                                    {sha ? (
                                        <span className={`${NOTE_CLASS} text-[10px] font-mono break-all`}>
                                            sha256 {sha}
                                        </span>
                                    ) : null}
                                    <div className={`${NOTE_CLASS} text-[10px] flex flex-wrap items-center gap-x-[6px]`}>
                                        {uploadedBy ? (
                                            <span>uploaded by <span className="text-[color:var(--muted)]">{uploadedBy}</span></span>
                                        ) : null}
                                        {createdTs ? <span>· {formatTs(createdTs)}</span> : null}
                                        {publishedTs ? <span>· published {formatTs(publishedTs)}</span> : null}
                                    </div>
                                    {typeof versionBusy[v.id] === "string" && versionBusy[v.id] !== "pending" ? (
                                        <span className="text-[10px] text-red-400" title={versionBusy[v.id]}>Publish failed</span>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="border-t border-[color:var(--line)]" />

            {isAdmin ? <div className="grid gap-[8px]">
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                    Rollout
                </span>
                {!rollouts.length ? (
                    <span className={`${NOTE_CLASS} text-[11px]`}>No rollout history.</span>
                ) : (
                    <ul className="grid gap-[8px]">
                        {rollouts.map((rollout) => {
                            const publicationId = rollout.publicationId;
                            const paused = rollout.publicationState === "ARTIFACT_PUBLICATION_STATE_PAUSED";
                            const rolling = rollout.publicationState === "ARTIFACT_PUBLICATION_STATE_ROLLING";
                            const manual = rollout.waveMode === "ARTIFACT_WAVE_MODE_MANUAL";
                            const target = rollout.targetKind === "all_endpoints"
                                ? "All current and future endpoints"
                                : "Endpoint-specific pin set";
                            return (
                                <li key={publicationId} className="grid gap-[6px] border border-[color:var(--line)] px-[10px] py-[8px]">
                                    <div className="flex flex-wrap items-center justify-between gap-[8px]">
                                        <span className="text-[12px] text-[color:var(--ink)]">
                                            v{rollout.version} to {target}
                                        </span>
                                        <span className={`${NOTE_CLASS} text-[10px] uppercase`}>
                                            {hasHoldableState(rollout) && rollout.held ? "Held" : String(rollout.publicationState || "")
                                                .replace("ARTIFACT_PUBLICATION_STATE_", "")
                                                .replaceAll("_", " ")}
                                        </span>
                                    </div>
                                    <div className={`${NOTE_CLASS} grid gap-[3px] text-[10px] sm:grid-cols-2`}>
                                        <span>Wave {rollout.currentPosition || 0}, {manual ? "manual" : "automatic"}</span>
                                        <span>{rollout.eligibleEndpointCount ?? 0} eligible / {rollout.activeEndpointCount ?? 0} active</span>
                                        <span>{rollout.appliedEndpointCount ?? 0} applied</span>
                                        <span>{rollout.bakeDeadline ? `Bake ends ${formatTs(rollout.bakeDeadline)}` : "No active bake"}</span>
                                    </div>
                                    <p className={`${NOTE_CLASS} m-0 text-[10px]`}>
                                        Endpoint-specific pins take precedence over the all-endpoints deployment for this Skill.
                                    </p>
                                    {isAdmin && canControlDelivery(rollout) ? (
                                        <div className="flex flex-wrap gap-[8px]">
                                            <button
                                                type="button"
                                                disabled={rolloutBusy[publicationId] === "pending"}
                                                onClick={() => void runRolloutCommand(
                                                    publicationId,
                                                    deliveryControlCommand,
                                                    deliveryControlCommands(rollout),
                                                )}
                                                className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50"
                                            >
                                                {deliveryStopped(rollout) ? "Resume" : "Hold"}
                                            </button>
                                            {(rolling || paused) && <button
                                                type="button"
                                                disabled={rolloutBusy[publicationId] === "pending"}
                                                onClick={() => void runRolloutCommand(
                                                    publicationId,
                                                    "setWaveMode",
                                                    {
                                                        publicationId,
                                                        waveMode: manual
                                                            ? "ARTIFACT_WAVE_MODE_AUTOMATIC"
                                                            : "ARTIFACT_WAVE_MODE_MANUAL",
                                                    },
                                                )}
                                                className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50"
                                            >
                                                Use {manual ? "automatic" : "manual"} waves
                                            </button>}
                                            {manual && (rolling || paused) ? (rollout.rolloutIds ?? []).map((rolloutId, index) => (
                                                <button
                                                    key={rolloutId}
                                                    type="button"
                                                    disabled={rolloutBusy[publicationId] === "pending"}
                                                    onClick={() => void runRolloutCommand(
                                                        publicationId,
                                                        "advanceWave",
                                                        { rolloutId },
                                                    )}
                                                    className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50"
                                                >
                                                    Advance lane {index + 1}
                                                </button>
                                            )) : null}
                                        </div>
                                    ) : null}
                                    {typeof rolloutBusy[publicationId] === "string" && rolloutBusy[publicationId] !== "pending" ? (
                                        <span className="text-[10px] text-red-400" title={rolloutBusy[publicationId]}>Rollout update failed</span>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div> : null}

            <div className="border-t border-[color:var(--line)]" />

            <div className="grid gap-[8px]">
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                    Deployments
                </span>
                {statusesTruncated ? (
                    <span className={`${NOTE_CLASS} text-[11px]`}>
                        Showing the most recent {formatCompact(statuses.length)} of {formatCompact(statusesTotal)} reported statuses.
                    </span>
                ) : null}
                {loading ? (
                    <span className={`${NOTE_CLASS} text-[11px]`}>Loading…</span>
                ) : !deployments.length ? (
                    <span className={`${NOTE_CLASS} text-[11px]`}>Not deployed.</span>
                ) : (
                    <ul className="grid gap-[8px]">
                        {deployments.map((d, i) => {
                            const hostname   = d.hostname ?? "";
                            const endpointId = d.endpointId ?? d.endpoint_id;
                            const verNum     = d.versionNumber ?? d.version_number;
                            const versionId  = d.versionId ?? d.version_id;
                            const deployedAt = d.deployedAt ?? d.deployed_at;
                            // Match per-endpoint+version. Falls back to endpoint-only
                            // in case the gateway omits version on the status row.
                            const status = statuses.find((s) =>
                                (s.endpointId ?? s.endpoint_id) === endpointId &&
                                (s.versionId  ?? s.version_id)  === versionId,
                            ) ?? statuses.find((s) =>
                                (s.endpointId ?? s.endpoint_id) === endpointId,
                            );
                            const errorCode = status?.errorCode ?? status?.error_code;
                            const message   = status?.message;
                            const reportedAt = status?.reportedAt ?? status?.reported_at;
                            const vendorOutcomes = status?.vendorOutcomes ?? status?.vendor_outcomes ?? [];
                            return (
                                <li key={i} className="grid gap-[4px] border border-[color:var(--line)] px-[10px] py-[8px]">
                                    <div className="flex items-center justify-between gap-[8px]">
                                        <span className="flex items-center gap-[8px] min-w-0">
                                            <span className="font-medium text-[12px] text-[color:var(--ink)] truncate">
                                                {hostname || endpointId || "All current and future endpoints"}
                                            </span>
                                            <span className="font-mono text-[10px] text-[color:var(--muted)]">v{verNum}</span>
                                        </span>
                                        <span className="flex items-center gap-[8px]">
                                            {status ? <DeploymentStatusBadge status={status.status} /> : null}
                                            {endpointId ? (
                                                deployBusy[endpointId] === "pending" ? (
                                                    <span className={`${NOTE_CLASS} text-[10px]`}>Revoking…</span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRevoke(endpointId)}
                                                        className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-red-400 cursor-pointer"
                                                        title="Remove this skill from this endpoint."
                                                    >
                                                        Revoke
                                                    </button>
                                                )
                                            ) : null}
                                        </span>
                                    </div>
                                    <div className={`${NOTE_CLASS} text-[10px] flex flex-wrap items-center gap-x-[6px]`}>
                                        {deployedAt ? <span>deployed {formatTs(deployedAt)}</span> : null}
                                        {reportedAt ? <span>· reported {formatTs(reportedAt)}</span> : null}
                                    </div>
                                    {errorCode || message ? (
                                        <div className="text-[11px] text-red-400 leading-[1.4]">
                                            {errorCode ? <span className="font-mono">{errorCode}</span> : null}
                                            {errorCode && message ? ": " : null}
                                            {message}
                                        </div>
                                    ) : null}
                                    {typeof deployBusy[endpointId] === "string" && deployBusy[endpointId] !== "pending" ? (
                                        <span className="text-[10px] text-red-400" title={deployBusy[endpointId]}>Revoke failed</span>
                                    ) : null}
                                    {vendorOutcomes.length ? (
                                        <ul className="grid gap-[2px] text-[10px] mt-[2px]">
                                            {vendorOutcomes.map((vo, j) => (
                                                <li key={j} className="flex items-center gap-[6px]">
                                                    <span className="text-[color:var(--muted)]">{vo.vendor}</span>
                                                    <span className="text-[color:var(--muted-soft)]">·</span>
                                                    <span className="text-[color:var(--ink)]">{skillDeploymentStatusLabel(vo.status)}</span>
                                                    {vo.errorCode || vo.error_code ? (
                                                        <span className="text-red-400 font-mono">{vo.errorCode ?? vo.error_code}</span>
                                                    ) : null}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="border-t border-[color:var(--line)]" />

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-end gap-[8px]">
                {typeof actionState === "string" && actionState.startsWith("archive-failed") ? (
                    <span className="text-[11px] text-red-400" title={actionState}>Archive failed</span>
                ) : null}
                {!skill.archived ? (
                    <button
                        type="button"
                        onClick={() => onDeploy(versions)}
                        disabled={loading || !versions.length}
                        className="h-[30px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] uppercase tracking-[0.04em] text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] disabled:opacity-50 cursor-pointer"
                    >
                        Deploy
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={handleArchive}
                    disabled={actionState === "archive-pending"}
                    className="h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-50 cursor-pointer"
                >
                    {actionState === "archive-pending" ? "Updating…" : (skill.archived ? "Unarchive" : "Archive")}
                </button>
                <button
                    type="button"
                    onClick={() => setDeleteOpen(true)}
                    className="h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:text-red-400 disabled:opacity-50 cursor-pointer"
                >
                    Delete
                </button>
            </div>

            <OverlayDialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                analyticsId={DIALOG.SKILL_DELETE}
                eyebrow="Skills"
                title="Delete skill"
                width="520px"
            >
                {deleteOpen ? (
                    <DeleteSkillDialog
                        skill={skillFull ?? skill}
                        versions={versions}
                        deployments={deployments}
                        onClose={() => setDeleteOpen(false)}
                        onConfirm={async () => {
                            await onDelete();
                            // Parent closes the drawer on success.
                        }}
                    />
                ) : null}
            </OverlayDialog>
        </div>
    );
}
